import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { RootLayout } from './RootLayout'
import { Login } from './pages/Login'
import { AuthProvider, RequireAuth } from './AuthProvider'
import { GqlProvider } from './GqlProvider'
import { ResetPassword } from './pages/ResetPassword'
import { NotFound } from './pages/Notfound'
import { Toaster } from 'sonner'
import { environment } from './environment'

import * as Sentry from '@sentry/react'
import { Dashboard } from './pages/Dashboard'
import { UserLogs } from './pages/UserLogs'
import { SwapTransaction } from './pages/SwapTransaction'
import { DepositTransaction } from './pages/DepositTransaction'
import { Company } from './pages/Company'
import { CompanyActivity } from './pages/CompanyActivity'
import { User } from './pages/User'
import { UserSettings } from './pages/UserSettings'
import { NavigationProvider } from './contexts/NavigationContext'
import { ErrorBoundary, RouterErrorBoundary } from '@valiant/ui-shared'
import { LogoProvider } from '@valiant/ui-shared'
import { GroupOwner } from './pages/GroupOwner'
import { ProfilePage } from './pages/mobile/ProfilePage'
import { NotificationsPage } from './pages/mobile/NotificationsPage'
import { DepositFundsPage } from './pages/mobile/DepositFundsPage'
import { SwapPage } from './pages/mobile/SwapPage'
import { AddWhitelistedAddressPage } from './pages/mobile/AddWhitelistedAddressPage'

Sentry.init({
  dsn: environment.SENTRY_DSN,
  sendDefaultPii: true,
  integrations: [Sentry.browserTracingIntegration()],
  tracePropagationTargets: [environment.API_URL],
  tracesSampleRate: environment.SENTRY_TRACES_SAMPLE_RATE,
})

// Dynamically set theme main color from env, if provided
const mainColor = environment.MAIN_COLOR as string | undefined
if (mainColor && typeof mainColor === 'string' && mainColor.trim()) {
  document.documentElement.style.setProperty('--main', mainColor.trim())
}
// Dynamically set theme destructive color from env, if provided
const destructiveColor = environment.DESTRUCTIVE_COLOR as string | undefined
if (destructiveColor && typeof destructiveColor === 'string' && destructiveColor.trim()) {
  document.documentElement.style.setProperty('--destructive', destructiveColor.trim())
}
const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { path: '/login', element: <Login />, errorElement: <RouterErrorBoundary /> },
      {
        path: '/reset-password/:token',
        element: <ResetPassword />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/',
        element: (
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/logs/:userId',
        element: (
          <RequireAuth>
            <UserLogs />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/transactions/swap/:transactionId',
        element: (
          <RequireAuth>
            <SwapTransaction />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/transactions/deposit/:depositId',
        element: (
          <RequireAuth>
            <DepositTransaction />
          </RequireAuth>
        ),
      },
      {
        path: '/companies/:companyId',
        element: (
          <RequireAuth>
            <Company />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/companies/:companyId/activity',
        element: (
          <RequireAuth>
            <CompanyActivity />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/group-owner/:userId',
        element: (
          <RequireAuth>
            <GroupOwner />
          </RequireAuth>
        ),
      },
      {
        path: '/users/:userId',
        element: (
          <RequireAuth>
            <User />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/users/:userId/settings',
        element: (
          <RequireAuth>
            <UserSettings />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/mobile/profile',
        element: (
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/mobile/notifications',
        element: (
          <RequireAuth>
            <NotificationsPage />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/mobile/deposit-funds',
        element: (
          <RequireAuth>
            <DepositFundsPage />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/mobile/swap',
        element: (
          <RequireAuth>
            <SwapPage />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/mobile/add-whitelisted-address',
        element: (
          <RequireAuth>
            <AddWhitelistedAddressPage />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      { path: '*', element: <NotFound />, errorElement: <RouterErrorBoundary /> },
    ],
  },
])

const msb = environment.MSB_LOGO || `/${environment.MSB}.svg` || '/local.svg'
const favicon = document.getElementById('dynamic-favicon') as HTMLLinkElement | null
if (favicon) {
  favicon.href = msb
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <LogoProvider msb={environment.MSB} msbLogo={environment.MSB_LOGO}>
      <ErrorBoundary>
        <AuthProvider>
          <GqlProvider>
            <NavigationProvider>
              <RouterProvider router={router} />
              <Toaster />
            </NavigationProvider>
          </GqlProvider>
        </AuthProvider>
      </ErrorBoundary>
    </LogoProvider>
  </StrictMode>,
)
